import React from 'react';
import {
    Button, Grid, Typography, Accordion, AccordionDetails,
    IconButton
} from '@mui/material';
import { AppToolbar } from '../../components/AppToolbar';
import { AppButton } from '../../components/AppButton';
import { Add, ArrowBack, Delete } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppBox } from '../../components/AppBox';
import { AppAutocompleteField } from '../../components/AppAutocompleteField';
import { ClassesType, ClassInstanceType, CreateClassesType } from '../../types/classes.type';
import { useForm, useWatch } from 'react-hook-form';
import { CRUDType } from '../../types/crud.type';
import { ClassesServices } from '../../services/classes.service';
import { useSnackbar } from '../../hooks/snackbar.hook';
import { AppTextField } from '../../components/AppTextField';
import { TrainersServices } from '../../services/trainers.service';
import { UsersType } from '../../types/users.type';
import { WorkoutType } from '../../types/workout.type';
import { DateUtils } from '../../utils/date.utils';
import { useSession } from '../../hooks/auth.hook';
import { PeriodizationsServices } from '../../services/periodizations.service';
import { PeriodizationType } from '../../types/periodizations.type';

export function ClassEdit({ mode }: CRUDType) {
    const navigate = useNavigate();
    const { user } = useSession();
    const { control, handleSubmit, setValue, getValues, watch } = useForm<CreateClassesType>({});
    const formValues = useWatch({
        control,
        defaultValue: {},
    });
    const periodizationId = Number(useWatch({ control, name: 'periodization_id' }));
    const weekDay = Number(useWatch({ control, name: 'week_day' }));
    const [params] = useSearchParams();
    const studioId = Number(params.get('studio_id'));
    const date = params.get('date');
    const { showSnackbar } = useSnackbar();

    const [trainers, setTrainers] = React.useState<UsersType[]>();
    const [periodizations, setPeriodizations] = React.useState<PeriodizationType[]>();
    const [workouts, setWorkouts] = React.useState<WorkoutType[]>();

    React.useEffect(() => {

        async function fetchData() {

            // Fetch trainers and periodizations in parallel
            const [trainersResp, periodiztionsResp] = await Promise.all([
                TrainersServices.getAll(),
                PeriodizationsServices.getAll()
            ]);

            const studioTrainers = trainersResp.data?.filter(t => t.user_studios?.find(s => s.studio_id == studioId));

            setTrainers(studioTrainers);
            setPeriodizations(periodiztionsResp.data);

            if (mode == 'edit') {
                try {
                    const resp = await ClassesServices.getByStudioAndDate(Number(studioId), date!)
                    const classData = resp.data;
                    if (classData) {
                        // Set all top-level fields
                        Object.entries(classData).forEach(([key, value]) => {
                            if (key !== 'instances') {
                                setValue(key as keyof ClassesType, value);
                            }
                        });

                        // Explicitly set the nested instances
                        if (classData.instances && classData.instances.length > 0) {
                            setValue('instances', classData.instances);
                        }
                    }
                } catch (error) {
                    const msg = (error as any).message || 'An error occurred';
                    console.error(msg, error);
                    showSnackbar(msg, 'error');
                }

            }

            handleRequiredSchedule()
        }

        fetchData();

    }, []);

    React.useEffect(() => {
        if (periodizationId && weekDay != null) {
            // Get the selected periodization
            const selected = periodizations?.find(p => p.id === periodizationId);

            // Filter workouts by weekday (and also include flexible workouts)
            const filteredWorkouts = selected?.periodization_workouts
                ?.filter(pw => pw.week_day === weekDay || pw.week_day === null)
                ?.map(pw => pw.workout)
                ?.filter((w): w is WorkoutType => !!w);

            setWorkouts(filteredWorkouts || []);
        } else {
            // If no valid periodization or weekday, clear workouts
            setWorkouts([]);
        }

    }, [periodizationId, weekDay]);

    const handleRequiredSchedule = (classData?: ClassesType) => {
        const currentInstances = classData?.instances || getValues('instances') || [];
        if (currentInstances.length < 1) {
            addSchedule();
        }
    };

    const addSchedule = () => {
        // Get the current schedules from the form state
        const currentSchedules = getValues('instances') || [];
        // Add a new empty schedule object to the array
        const newSchedule: ClassInstanceType = {
            studio_id: studioId!
        };

        // Update the schedules in the form state
        setValue('instances', [...currentSchedules, newSchedule]);
    };

    const handleRemoveSchedule = (index: number) => {
        const currentSchedules = getValues('instances') || [];
        if (currentSchedules.length > 1) {
            currentSchedules.splice(index, 1);
            setValue('instances', currentSchedules);
        } else {
            showSnackbar('At least one schedule is required.', 'warning');
        }
    };

    const handleSave = async (data: ClassesType) => {
        try {
            const isCreate = mode == 'create';
            const action = isCreate ? 'created' : 'updated';
            const serviceFn = isCreate ? ClassesServices.add : ClassesServices.update;

            await serviceFn(data);

            showSnackbar(`Class ${action} successfully!`, 'success');
            handleBack();
        } catch (error) {
            const msg = (error as any).response?.data?.message || (error as any).message || 'An error occurred';
            console.error(msg, error);
            showSnackbar(msg, 'error');
        }
    };



    const handleBack = () => {
        navigate(-1);
    }

    const transformDate = (value: Date) => {
        return DateUtils.toISODateFormat(value);
    }
    const pageMode = mode === 'edit' ? `Edit` : `Add`;

    const title = `${pageMode} ${user?.user_studios.find(s => s.studio_id == studioId!)?.studio.name} Class`
    const workoutDisabled = !periodizationId || !weekDay && weekDay !== 0;
    return (
        <form onSubmit={handleSubmit(handleSave)}>
            <AppToolbar title={title}>
                <Grid container spacing={2} style={{ flex: 0.3 }}>
                    <Grid item>
                        <AppButton type='submit'>
                            Save
                        </AppButton>
                    </Grid>
                    <Grid item>
                        <AppButton startIcon={<ArrowBack />} onClick={handleBack}>
                            Back
                        </AppButton>
                    </Grid>
                </Grid>
            </AppToolbar>
            <AppBox>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <AppAutocompleteField
                            disabled={mode == 'edit'}
                            control={control}
                            rules={{ required: 'Field is required' }}
                            multiple={false}
                            name={`periodization_id`}
                            label="Periodization"
                            service={{
                                options: periodizations
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <AppAutocompleteField
                            disabled={mode == 'edit'}
                            control={control}
                            multiple={false}
                            name="week_day"
                            label="Day of the Week"
                            service={{
                                options: [
                                    { id: 0, name: 'Sunday' },
                                    { id: 1, name: 'Monday' },
                                    { id: 2, name: 'Tuesday' },
                                    { id: 3, name: 'Wednesday' },
                                    { id: 4, name: 'Thursday' },
                                    { id: 5, name: 'Friday' },
                                    { id: 6, name: 'Saturday' }
                                ]
                            }}
                        />
                    </Grid>
                </Grid>
            </AppBox>
            {/* Schedule Section */}
            <AppBox>
                <Grid container spacing={2}>
                    {formValues.instances?.map((schedule: ClassInstanceType, scheduleIndex: number) => (
                        <Grid item xs={12} key={scheduleIndex + schedule.start_time!!}>
                            <Accordion expanded>
                                <AppBox sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>
                                        Schedule {scheduleIndex + 1}
                                    </Typography>
                                    <IconButton
                                        color='error'
                                        onClick={() => handleRemoveSchedule(scheduleIndex)} sx={{ ml: 'auto' }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </AppBox>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2}>
                                            <AppTextField
                                                control={control}
                                                rules={{ required: 'Field is required' }}
                                                name={`instances[${scheduleIndex}].start_time`}
                                                label="Time"
                                                type="time"
                                                inputProps={{
                                                    step: 300, // 5 min steps
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <AppAutocompleteField
                                                control={control}
                                                disabled={workoutDisabled}
                                                rules={{ required: 'Field is required' }}
                                                multiple={false}
                                                name={`instances[${scheduleIndex}].workout_id`}
                                                label="Workout"
                                                service={{
                                                    options: workouts
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <AppAutocompleteField
                                                control={control}
                                                rules={{ required: 'Field is required' }}
                                                multiple={false}
                                                name={`instances[${scheduleIndex}].trainer_id`}
                                                label="Trainer"
                                                service={{
                                                    options: trainers
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button startIcon={<Add />} onClick={addSchedule} variant="contained" sx={{ mt: 1, mb: 2 }}>
                            Add Schedule
                        </Button>
                    </Grid>
                </Grid>
            </AppBox>
        </form>
    );
};
